import { Dialog, DialogTitle, Typography, IconButton, DialogContent, Box, TextField, Grid, DialogActions, Button } from '@mui/material'
import React, { useState, useEffect } from 'react'
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from 'react-hook-form';
import AuthService from '../services/auth.service';
import { toast } from 'react-toastify';
import { EVENT_TYPES, logEvent, SIGNUP_STEPS } from '../utils/events.util';


interface AccountVerificationProps {
    open: boolean;
    onProceed: () => void;
    onClose: () => void;
    phoneNumber: string;
    email: string;
    password?: string;
    companyId: string;
    companyName: string;
    infLeadSquaredId: string;
}

const AccountVerification: React.FC<AccountVerificationProps> = ({ open, onClose, onProceed, phoneNumber, email, password, companyId, companyName, infLeadSquaredId }) => {
    const [timeLeft, setTimeLeft] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    
    const { control, watch, setValue } = useForm({
        defaultValues: {
            otp: ['', '', '', '', '', '']
        }
    });

    const otpValues: string[] = watch('otp') || ['', '', '', '', '', ''];

    useEffect(() => {
        if (!open) {
            setTimeLeft(20);
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 0) {
                    clearInterval(timer);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [open]);

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace') {
            if (otpValues && !otpValues[index] && index > 0) {
                const prevInput = document.querySelector(
                    `input[name="otp.${index - 1}"]`
                ) as HTMLInputElement;
                if (prevInput) {
                    prevInput.focus();
                }
            }
        }
    };

    const handlePaste = (event: React.ClipboardEvent) => {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text');
        const pastedNumbers = pastedData.replace(/[^\d]/g, '').slice(0, 6);
        
        if (pastedNumbers && otpValues) {
            const newOtp = [...otpValues];
            pastedNumbers.split('').forEach((digit, index) => {
                if (index < 6) newOtp[index] = digit;
            });
            newOtp.forEach((value, index) => {
                setValue(`otp.${index}`, value);
            });
        }
    };

    const handleResendCode = async () => {
        if (timeLeft === 0) {
            try {
                await AuthService.resendConfirmationCode(email);
                toast.success('Code resent successfully');
                setTimeLeft(20);
                
                const timer = setInterval(() => {
                    setTimeLeft((prevTime) => {
                        if (prevTime <= 0) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prevTime - 1;
                    });
                }, 1000);
            } catch (error: any) {
                toast.error('Failed to resend code');
                console.error('Failed to resend code:', error.message);
            }
        }
    };

    const formatPhoneNumber = (phone: string) => {
        const digits = phone?.replace(/\D/g, '');
        const lastTwoDigits = digits.slice(-2);
        return `*********${lastTwoDigits}`;
    };

    const handleClose = () => {
        [0, 1, 2, 3, 4, 5].forEach((index) => {
            setValue(`otp.${index}`, '');
        });
        onClose();
    };

    const handleProceed = async () => {
        const confirmationCode = otpValues?.join('') || '';
        
        // Check if all 6 digits are entered
        if (confirmationCode.length !== 6) {
            toast.error('Please enter the complete 6-digit code');
            return;
        }
        
        try {
            setIsLoading(true);
            await AuthService.confirmSignup({
                email,
                confirmationCode,
                password: password || '',
                infLeadSquaredId,
                companyId,
                companyName
            });

            logEvent(email, EVENT_TYPES.SIGNUP, {
                step: SIGNUP_STEPS.COMPLETED
            });
            
            toast.success('Account verified successfully, Please check your email for the login details');
            onProceed();
        } catch (error: any) {
            toast.error(error.message);
            console.error('Confirmation failed:', error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth sx={{
            "& .MuiDialog-paper": {
                maxWidth: "506px",
                borderRadius: "18px",
                position: "relative",
                bgcolor: '#ffffff',
                border: '1.5px solid #e5e7eb',
                boxShadow: '0 4px 32px 0 rgba(0,0,0,0.15)',
            },
        }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                }}
            >
                <Typography
                    sx={{
                        fontSize: {
                            xs: "22px",
                        },
                        fontWeight: "700",
                        color: "#1f2937",
                    }}
                >
                    Account verification
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: "#6b7280",
                        position: "absolute",
                        top: "12px",
                        right: "16px",
                        "&:hover": {
                            color: "#374151",
                            bgcolor: 'rgba(0,0,0,0.05)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ padding: "32px 20px", borderBottom: "1px solid #e5e7eb" }}>
                <Typography color='#374151' fontSize='18px' textAlign='center' mb='32px'>
                    Enter the 6-digit code we just sent to your email <Typography component="span" fontSize="inherit" fontStyle="italic" color="#6066E9">{email}</Typography>
                </Typography>
                <Box display="flex" gap="12px" justifyContent="center" mb='27px' flexWrap='wrap'>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                        <Controller
                            key={index}
                            name={`otp.${index}`}
                            control={control}
                            rules={{
                                pattern: /^[0-9]*$/
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    inputProps={{
                                        maxLength: 1,
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!/^\d*$/.test(value)) return;
                                        
                                        field.onChange(value.slice(-1));
                                        
                                        if (value && index < 5) {
                                            const nextInput = document.querySelector(
                                                `input[name="otp.${index + 1}"]`
                                            ) as HTMLInputElement;
                                            if (nextInput) {
                                                nextInput.focus();
                                            }
                                        }
                                    }}
                                    onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
                                    onPaste={handlePaste}
                                    sx={{
                                        width: '48px',
                                        "& .MuiOutlinedInput-root": {
                                            width: '48px',
                                            height: '48px',
                                            backgroundColor: "#f9fafb",
                                            borderRadius: '8px',
                                            border: '1px solid #d1d5db',
                                            transition: 'border-color 0.2s',
                                            "&:hover": {
                                                borderColor: '#6066E9',
                                            },
                                            "&.Mui-focused": {
                                                borderColor: '#6066E9',
                                            },
                                            "& input": {
                                                padding: '0',
                                                textAlign: 'center',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                color: '#1f2937',
                                            }
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: 'none',
                                        },
                                        "& .MuiOutlinedInput-input": {
                                            height: '100%',
                                        }
                                    }}
                                />
                            )}
                        />
                    ))}
                </Box>
                <Typography 
                    color='#6066E9' 
                    fontSize='15px' 
                    textAlign='center' 
                    fontWeight='500'
                    sx={{ 
                        cursor: timeLeft === 0 ? 'pointer' : 'default',
                        '&:hover': timeLeft === 0 ? {
                            textDecoration: 'underline',
                            color: '#4B4EB5'
                        } : {}
                    }}
                    onClick={handleResendCode}
                >
                    {timeLeft === 0 ? (
                        'Resend code'
                    ) : (
                        `Resend code in ${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(2, '0')} sec`
                    )}
                </Typography>
            </DialogContent>
            {/* Dialog Actions */}
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "27px 20px 20px 20px",
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClose}
                    sx={{
                        textTransform: "none",
                        px: "24px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        color: "#6b7280",
                        mr: "16px",
                        height: "47px!important",
                        fontWeight: 600,
                        "&:hover": {
                            borderColor: "#6066E9",
                            color: "#6066E9",
                            bgcolor: 'rgba(96,102,233,0.05)',
                        }
                    }}
                >
                    Back
                </Button>

                <Button 
                    variant="contained" 
                    className="x-large-btn" 
                    disableElevation 
                    disabled={isLoading || otpValues.join('').length !== 6}
                    sx={{
                        textTransform: "none", 
                        borderRadius: "8px", 
                        px: "28px", 
                        height: "47px!important",
                        bgcolor: '#6066E9',
                        color: '#ffffff',
                        fontWeight: 600,
                        '&:hover': { 
                            bgcolor: '#4B4EB5' 
                        },
                        '&:disabled': { 
                            bgcolor: '#d1d5db', 
                            color: '#9ca3af' 
                        }
                    }} 
                    onClick={handleProceed}
                >
                    {isLoading ? "Verifying..." : "Proceed"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
export default AccountVerification
